import React from "react";
import styled, { css } from "styled-components";
import ContentLoader from "react-content-loader";
import { tokens, surface, hairline } from "../styles/designTokens";

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusMd};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

const TAG_PALETTE = [
  '#dc2626', '#ea580c', '#65a30d', '#16a34a', '#0d9488',
  '#0891b2', '#2563eb', '#4f46e5', '#9333ea', '#db2777',
];

export function getTagColor(seedText) {
  const seed = (seedText || '').toString().trim().toLowerCase();
  if (!seed) return TAG_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
}

export function formatCompact(num) {
  if (!num || num === 0) return "R$ 0";
  const absNum = Math.abs(num);
  if (absNum >= 1000000) return `R$ ${(num / 1000000).toFixed(1)}M`;
  if (absNum >= 1000) return `R$ ${(num / 1000).toFixed(1)}K`;
  return `R$ ${num.toFixed(0)}`;
}

export function formatNumber(num) {
  if (!num) return "R$ 0,00";
  return `R$ ${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: 12px;
  padding: 14px 18px;
  margin-bottom: 1px;
  ${({ theme }) => cardBase(theme)};
  border-radius: ${tokens.radiusMd};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
  cursor: pointer;
  opacity: 0.85;
  align-items: center;
  min-width: 0;

  &:hover {
    border-color: ${tokens.brandRing};
    background: ${({ theme }) => surface(theme, 1)};
    opacity: 1;
  }
`;

const InfoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
`;

const EntityIcon = styled.div`
  flex-shrink: 0;
  font-size: 1.1em;
  color: ${({ $color }) => $color || tokens.neutral};
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
`;

const Eyebrow = styled.div`
  ${mono};
  font-size: 0.68em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.div`
  font-size: 0.9em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tags = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 3px;
`;

const TagColor = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: ${tokens.radiusPill};
  font-family: ${tokens.fontSans};
  font-size: 0.62em;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.5;
  color: #fff;
  text-transform: capitalize;
  white-space: nowrap;
  background: ${({ $color }) => $color};
`;

const TagNeutral = styled.span`
  ${mono};
  font-size: 0.58em;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: ${tokens.radiusPill};
  background: ${({ theme }) => surface(theme, 1)};
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  border: 1px solid ${({ theme }) => hairline(theme)};
  white-space: nowrap;
`;

const StatCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;

  &:last-child {
    justify-content: flex-end;
  }
`;

const StatValue = styled.span`
  ${mono};
  font-size: 0.82em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  opacity: 0.65;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: 12px;
  padding: 14px 18px;
  margin-bottom: 1px;
  ${({ theme }) => cardBase(theme)};
  border-radius: ${tokens.radiusMd};
  align-items: center;
  min-width: 0;
  opacity: 0.85;
  pointer-events: none;
`;

const SkeletonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
`;

const SkeletonIcon = styled.div`
  flex-shrink: 0;
  width: 1.1em;
  height: 1.1em;
  border-radius: 2px;
  background: ${({ theme }) => hairline(theme, true)};
  opacity: 0.3;
`;

const SkeletonBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
`;

const SkeletonTags = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
`;

const SkeletonTag = styled.div`
  height: 14px;
  border-radius: ${tokens.radiusPill};
  background: ${({ theme }) => hairline(theme, true)};
  opacity: 0.3;
  width: ${({ $w }) => $w || '50px'};
`;

export function EntityListSkeleton({ columns = [], rowCount = 8 }) {
  const gridColumns = columns.map((c) => c.width || "minmax(0, 1fr)").join(" ");
  const isDark = typeof window !== "undefined" && document.body.classList.contains("dark");
  const bg = isDark ? "#1a1a1a" : "#f0f0f0";
  const fg = isDark ? "#2a2a2a" : "#e5e7eb";

  return (
    <>
      {[...Array(rowCount)].map((_, i) => (
        <SkeletonRow key={`sk-${i}`} $columns={gridColumns}>
          {/* Info cell */}
          <SkeletonInfo>
            <SkeletonIcon />
            <SkeletonBars>
              <ContentLoader speed={2} width={80} height={10} backgroundColor={bg} foregroundColor={fg}>
                <rect x="0" y="0" rx="3" ry="3" width="80" height="10" />
              </ContentLoader>
              <ContentLoader speed={2} width={180} height={14} backgroundColor={bg} foregroundColor={fg}>
                <rect x="0" y="0" rx="3" ry="3" width="180" height="14" />
              </ContentLoader>
              <SkeletonTags>
                <SkeletonTag $w="50px" />
                <SkeletonTag $w="30px" />
              </SkeletonTags>
            </SkeletonBars>
          </SkeletonInfo>

          {/* Stat cells */}
          {columns.slice(1).map((_, j) => (
            <StatCell key={j}>
              <ContentLoader speed={2} width={60} height={13} backgroundColor={bg} foregroundColor={fg}>
                <rect x="0" y="0" rx="3" ry="3" width="60" height="13" />
              </ContentLoader>
            </StatCell>
          ))}
        </SkeletonRow>
      ))}
    </>
  );
}

export default function EntityListRow({
  entity,
  color,
  icon: Icon,
  eyebrow,
  title,
  tags = [],
  columns = [],
  onClick,
}) {
  return (
    <Row
      $columns={columns.map((c) => c.width || "minmax(0, 1fr)").join(" ")}
      $color={color}
      onClick={onClick}
    >
      {/* Primeira célula: Info (ícone + detalhes + tags) */}
      <InfoCell>
        {Icon && (
          <EntityIcon $color={color}>
            <Icon />
          </EntityIcon>
        )}
        <Details>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          {title && <Title>{title}</Title>}
          {tags.length > 0 && (
            <Tags>
              {tags.map((tag, i) =>
                tag.neutral ? (
                  <TagNeutral key={i}>{tag.label}</TagNeutral>
                ) : (
                  <TagColor key={i} $color={tag.color || getTagColor(tag.label)}>
                    {tag.label}
                  </TagColor>
                )
              )}
            </Tags>
          )}
        </Details>
      </InfoCell>

      {/* Demais colunas */}
      {columns.slice(1).map((col, i) => (
        <StatCell key={i}>
          <StatValue>
            {typeof col.render === "function"
              ? col.render(entity)
              : col.value || "—"}
          </StatValue>
        </StatCell>
      ))}
    </Row>
  );
}
